import React, { useEffect, useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../../components/ui/sheet';
import { useIsMobile } from '../../hooks/use-mobile';
import { addDays, buildMoveRequest, describeDates, previewTargets, shortDay } from './businessDate';
import './importReview.css';

const initialForm = (dates) => ({
  mode: 'set',
  date: dates.length === 1 ? dates[0] : (dates[0] || ''),
  shift: '',
  updateFileRecord: true,
  refreshDashboard: true,
  note: '',
});

/**
 * "Change business date" — set one date for every row of the import, or shift
 * every row by N days. Bottom sheet on phones, side sheet on desktop.
 *   dates     distinct current row dates ('YYYY-MM-DD', sorted)
 *   counts    { revenue, roomStats } rows that will move
 *   onSubmit  async (body) => void — throws to keep the sheet open
 */
export default function BusinessDateSheet({ open, onOpenChange, dates, counts, subtitle, onSubmit }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState(() => initialForm(dates));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initialForm(dates));
      setError(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- reset only when opened

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setError(null); };
  const total = (counts.revenue || 0) + (counts.roomStats || 0);
  const targets = previewTargets(form, dates);
  const single = dates.length === 1 ? dates[0] : null;

  const submit = async (e) => {
    e.preventDefault();
    const { body, error: problem } = buildMoveRequest(form, dates);
    if (problem) { setError(problem); return; }
    setSaving(true);
    try {
      await onSubmit(body);
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not change the business date.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        hideClose
        className={`ir-sheet${isMobile ? ' ir-sheet-bottom' : ' ir-sheet-side'}`}
      >
        <form onSubmit={submit} className="ir-form">
          {isMobile ? <span className="ir-grab" aria-hidden="true" /> : null}
          <div className="ir-sheet-head">
            <div>
              <SheetTitle className="ir-title">Change business date</SheetTitle>
              <SheetDescription className="ir-sub">{subtitle}</SheetDescription>
            </div>
            <button type="button" className="ir-icon-btn" aria-label="Close" onClick={() => onOpenChange(false)} disabled={saving}>
              <X aria-hidden="true" />
            </button>
          </div>

          <div className="ir-seg" role="radiogroup" aria-label="How to change">
            {[['set', 'Set a date'], ['shift', 'Shift by days']].map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={form.mode === value}
                onClick={() => set({ mode: value })}
              >
                {label}
              </button>
            ))}
          </div>

          {form.mode === 'set' ? (
            <>
              <label className="ir-field">
                <span>New business date</span>
                <input className="ir-input ir-mono" type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} required />
              </label>
              {single ? (
                <div className="ir-chips">
                  <button type="button" className="ir-chip" onClick={() => set({ date: addDays(single, -1) })}>Day before</button>
                  <button type="button" className="ir-chip" onClick={() => set({ date: addDays(single, 1) })}>Day after</button>
                </div>
              ) : (
                <p className="ir-help">Every row moves to this one date.</p>
              )}
            </>
          ) : (
            <label className="ir-field">
              <span>Days to shift every row</span>
              <input
                className="ir-input ir-mono"
                type="number"
                inputMode="numeric"
                step="1"
                placeholder="-1 or 1"
                value={form.shift}
                onChange={(e) => set({ shift: e.target.value })}
              />
              <span className="ir-help">Negative moves earlier. Rows keep their spacing.</span>
            </label>
          )}

          <div className="ir-preview" aria-live="polite">
            <div><span className="ir-help">From</span><strong className="ir-mono">{describeDates(dates)}</strong></div>
            <ArrowRight aria-hidden="true" />
            <div className="ir-preview-to">
              <span>To</span>
              <strong className="ir-mono">{targets.length ? describeDates([...new Set(targets)].sort()) : '—'}</strong>
            </div>
          </div>
          <p className="ir-help ir-strong">
            {counts.revenue || 0} revenue and {counts.roomStats || 0} room stat rows move
            {single && form.mode === 'set' && targets[0] ? ` from ${shortDay(single)} to ${shortDay(targets[0])}` : ''}.
          </p>

          <label className="ir-field">
            <span>Reason <em>(optional)</em></span>
            <input className="ir-input" type="text" value={form.note} placeholder="e.g. night audit ran after midnight" onChange={(e) => set({ note: e.target.value })} maxLength={500} />
          </label>

          <label className="ir-check">
            <input type="checkbox" checked={form.updateFileRecord} onChange={(e) => set({ updateFileRecord: e.target.checked })} />
            <span>Update the file's business date too<small>Status, Calendar and Documents follow the new date</small></span>
          </label>
          <label className="ir-check">
            <input type="checkbox" checked={form.refreshDashboard} onChange={(e) => set({ refreshDashboard: e.target.checked })} />
            <span>Refresh the dashboard for the old and new dates</span>
          </label>

          {error ? <p className="ir-error" role="alert">{error}</p> : null}

          <div className="ir-actions">
            <button type="button" className="ir-btn" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</button>
            <button type="submit" className="ir-btn ir-btn-primary" disabled={saving || total === 0}>
              {saving ? 'Moving…' : `Move ${total} row${total === 1 ? '' : 's'}`}
            </button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
